import {
    sendChatMessage,
    subscribeToChat,
    subscribeToIntelligenceMonitors, // Added import
} from '@/services/intelligenceWidgets.js';
import { fetchAuthSession } from 'aws-amplify/auth';
import React from 'react';
import { createPortal } from 'react-dom';
import { useStorylineChatContext } from './StorylineChatContext.tsx';

// Added Markdown-style rendering helper
const renderMessageContent = (content: string): React.ReactNode => {
  const lines = content.split(/\r?\n/);
  const nodes: React.ReactNode[] = [];
  let currentList: string[] = [];

  const flushList = () => {
    if (!currentList.length) return;
    nodes.push(
      <ul
        style={{
          paddingLeft: 16,
          margin: '2px 0',
        }}
      >
        {currentList.map((item, index) => (
          <li key={index} style={{ marginBottom: 2 }}>
            {item}
          </li>
        ))}
      </ul>
    );
    currentList = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ')) {
      currentList.push(trimmed.slice(2));
      return;
    }
    flushList();
    if (trimmed.length) {
      nodes.push(
        <p key={`p-${index}`} style={{ margin: '2px 0' }}>
          {trimmed}
        </p>
      );
    }
  });

  flushList();

  if (!nodes.length) {
    return <span>{content}</span>;
  }

  return <>{nodes}</>;
};

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

type ToolStatus = {
  id: string;
  label: string;
  state: 'running' | 'done' | 'error';
  detail?: string;
};

// Added IntelligenceMonitorEvent type for monitor event subscription
type IntelligenceMonitorEvent = {
  profileId: string;
  storylineId: string;
  title: string;
  summary?: string;
  ts: number;
};

const TOOL_LABELS: Record<string, string> = {
  getProfileStorylines: 'Collecting storylines',
  getStorylineDetails: 'Loading storyline details',
  getInstrumentStorylines: 'Instrument storylines',
  getProfileFilings: 'Recent filings',
  getExposureFromFilings: 'Exposure snapshot',
  getMarketSnapshot: 'Market snapshot',
};

const formatToolLabel = (toolName?: string) => {
  if (!toolName) return 'Working';
  return TOOL_LABELS[toolName] || toolName.replace(/([A-Z])/g, ' $1').trim();
};

interface StorylineChatProps {
  profileId?: string;
  ticker?: string | null;
  title?: string;
}

export const StorylineChat: React.FC<StorylineChatProps> = ({
  profileId,
  ticker,
  title = 'Ask Akem about these storylines',
}) => {
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [toolStatuses, setToolStatuses] = React.useState<ToolStatus[]>([]);
  const clearStatusesTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { activeStoryline } = useStorylineChatContext();
  // New state for monitor events
  const [monitorEvents, setMonitorEvents] = React.useState<
    { id: string; title: string; summary?: string; ts: number }[]
  >([]);
  const monitorClearTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derived ticker and storyline bucket context
  const derivedTicker = React.useMemo(() => {
    if (!activeStoryline) return undefined;
    const instrumentId =
      (activeStoryline as any).instrumentId ||
      (activeStoryline as any).instrument_id;
    if (!instrumentId || typeof instrumentId !== 'string') return undefined;
    const parts = instrumentId.split(':');
    const sym = parts[parts.length - 1]?.trim();
    return sym || undefined;
  }, [activeStoryline]);

  const storylineContext = React.useMemo(
    () =>
      activeStoryline
        ? {
            id: activeStoryline.id,
            title: activeStoryline.title,
            bucket:
              (activeStoryline as any).bucket ||
              (activeStoryline as any).bucketInference?.bucket ||
              (activeStoryline as any).domain,
            summary: activeStoryline.summary,
          }
        : undefined,
    [activeStoryline]
  );

  const effectiveTicker = React.useMemo(
    () => (ticker || derivedTicker || undefined) ?? undefined,
    [ticker, derivedTicker]
  );

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const node = containerRef.current;
      if (!node) return;
      if (!node.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for streaming events
  React.useEffect(() => {
    const unsubscribe = subscribeToChat((event) => {
      if (event.type === 'text-delta') {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant') {
            return [
              ...prev.slice(0, -1),
              { ...last, content: last.content + event.text },
            ];
          }
          // If the last message wasn't assistant (e.g. first token), append new assistant message
          return [...prev, { role: 'assistant', content: event.text }];
        });
        setLoading(false); // Start showing as soon as we get text
      } else if (event.type === 'tool-call') {
        const id = String((event as any).toolCallId || `${event.toolName}-${Date.now()}`);
        const label = formatToolLabel((event as any).toolName);
        setToolStatuses((prev) => {
          const next = prev.filter((s) => s.id !== id);
          const running: ToolStatus = { id, label, state: 'running' };
          next.push(running);
          return next.slice(-6);
        });
      } else if (event.type === 'tool-result') {
        const id = String((event as any).toolCallId || `${event.toolName}-${Date.now()}`);
        const label = formatToolLabel((event as any).toolName);
        const result = (event as any).result;
        const isError = !!(result && result.error);
        const detail =
          typeof result?.error === 'string'
            ? result.error
            : typeof result?.message === 'string'
            ? result.message
            : undefined;
        const updatedState: ToolStatus['state'] = isError ? 'error' : 'done';
        const updated: ToolStatus = { id, label, state: updatedState, detail };
        setToolStatuses((prev) => {
          const next = prev.some((s) => s.id === id)
            ? prev.map((s) =>
                s.id === id
                  ? { ...s, state: updatedState, detail: detail ?? s.detail }
                  : s
              )
            : [...prev, updated];
          return next.slice(-6);
        });
      } else if (event.type === 'error') {
        setError(event.error);
        setLoading(false);
        setToolStatuses([]);
      } else if (event.type === 'done') {
        setLoading(false);
        if (clearStatusesTimeout.current) {
          clearTimeout(clearStatusesTimeout.current);
        }
        clearStatusesTimeout.current = setTimeout(() => {
          setToolStatuses([]);
        }, 1400);
      }
    });
    return () => {
      unsubscribe();
      if (clearStatusesTimeout.current) {
        clearTimeout(clearStatusesTimeout.current);
      }
    };
  }, []);

  // New useEffect for monitor events
  React.useEffect(() => {
    if (!profileId) {
      setMonitorEvents([]);
      if (monitorClearTimeout.current) {
        clearTimeout(monitorClearTimeout.current);
        monitorClearTimeout.current = null;
      }
      return;
    }
    const unsubscribe = subscribeToIntelligenceMonitors(
      (event: IntelligenceMonitorEvent) => {
        const id = `${event.profileId}:${event.storylineId}:${event.ts}`;
        setMonitorEvents((prev) => {
          const next = [
            {
              id,
              title: event.title,
              summary: event.summary,
              ts: event.ts,
            },
            ...prev.filter((item) => item.id !== id),
          ];
          return next.slice(0, 3);
        });
        if (monitorClearTimeout.current) {
          clearTimeout(monitorClearTimeout.current);
        }
        monitorClearTimeout.current = setTimeout(() => {
          setMonitorEvents([]);
        }, 15000);
      },
      { profileIds: [profileId] }
    );
    return () => {
      unsubscribe();
      if (monitorClearTimeout.current) {
        clearTimeout(monitorClearTimeout.current);
        monitorClearTimeout.current = null;
      }
      setMonitorEvents([]);
    };
  }, [profileId]);

  const handleSubmit = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    if (loading) return;
    const prompt = input.trim();
    if (!prompt) return;
    const nextMessages: ChatMessage[] = [
      ...messages,
      { role: 'user', content: prompt },
    ];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    setError(null);
    setOpen(true);
    if (clearStatusesTimeout.current) {
      clearTimeout(clearStatusesTimeout.current);
      clearStatusesTimeout.current = null;
    }
    setToolStatuses([]);

    try {
      // Get the auth token
      let token: string | undefined;
      try {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString();
      } catch (err) {
        console.warn('[StorylineChat] Failed to get auth token', err);
      }

      sendChatMessage({
        messages: nextMessages,
        profileId,
        ticker: effectiveTicker, // Used effectiveTicker
        storylineContext,        // Used storylineContext
        token,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send message'
      );
      setLoading(false);
    }
  };

  const handleFocusInput = () => {
    setOpen(true);
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 16,
        transform: 'translateX(-50%)',
        maxWidth: 720,
        width: 'calc(100% - 32px)',
        zIndex: 5000, // keep above storyline modals and overlays
        pointerEvents: 'none',
      }}
    >
      {/* Frosted glass modal above the input */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 56,
          transform: open
            ? 'translate(-50%, 0)'
            : 'translate(-50%, 8px)',
          width: '100%',
          maxHeight: 320,
          borderRadius: 16,
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.35)',
          background: 'rgba(15, 23, 42, 0.96)',
          color: '#e5e7eb',
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(148, 163, 184, 0.5)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 150ms ease, transform 150ms ease',
        }}
      >
        <div style={{ fontWeight: 500, fontSize: 13, opacity: 0.9 }}>
          {title}
        </div>
        {activeStoryline && (
          <div
            style={{
              marginTop: 2,
              marginBottom: 2,
              fontSize: 11,
              color: '#a5b4fc',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 9999,
                background: 'rgba(79, 70, 229, 0.16)',
                border: '1px solid rgba(129, 140, 248, 0.6)',
              }}
            >
              Storyline context
            </span>
            <span
              style={{
                maxWidth: 340,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={activeStoryline.title}
            >
              {activeStoryline.title}
            </span>
          </div>
        )}
        <div style={{ fontSize: 12, color: '#9ca3af' }}>
          Ask follow-up questions about the latest storylines, regulatory filings, or live
          market context for this profile.
        </div>
        {toolStatuses.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 4,
            }}
          >
            {toolStatuses.map((status) => {
              const isError = status.state === 'error';
              const isDone = status.state === 'done';
              const bg = isError
                ? 'linear-gradient(120deg, rgba(239,68,68,0.26), rgba(127,29,29,0.35))'
                : isDone
                ? 'linear-gradient(120deg, rgba(34,197,94,0.22), rgba(21,128,61,0.3))'
                : 'linear-gradient(120deg, rgba(59,130,246,0.2), rgba(79,70,229,0.3))';
              const border = isError
                ? '1px solid rgba(248,113,113,0.55)'
                : isDone
                ? '1px solid rgba(74,222,128,0.6)'
                : '1px solid rgba(129,140,248,0.6)';
              const icon = isError ? '⚠️' : isDone ? '✓' : '⋯';
              return (
                <span
                  key={status.id}
                  title={status.detail || status.label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    borderRadius: 12,
                    background: bg,
                    border,
                    color: isError ? '#fecdd3' : isDone ? '#bbf7d0' : '#c7d2fe',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.1,
                    boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
                  }}
                >
                  <span style={{ fontSize: 13 }}>{icon}</span>
                  <span>{status.label}</span>
                  {status.detail && isError ? (
                    <span style={{ opacity: 0.75, fontWeight: 500 }}>{status.detail}</span>
                  ) : null}
                </span>
              );
            })}
          </div>
        )}
        {monitorEvents.length > 0 && ( // Added monitor events rendering
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              marginTop: 6,
            }}
          >
            {monitorEvents.map((evt) => (
              <div
                key={evt.id}
                title={evt.summary || evt.title}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  borderRadius: 12,
                  background:
                    'linear-gradient(120deg, rgba(16,185,129,0.18), rgba(5,150,105,0.32))',
                  border: '1px solid rgba(52,211,153,0.75)',
                  color: '#bbf7d0',
                  fontSize: 12,
                  fontWeight: 500,
                  boxShadow: '0 10px 22px rgba(0,0,0,0.4)',
                }}
              >
                <span style={{ fontSize: 13 }}>🔔</span>
                <span>New monitor update</span>
                <span
                  style={{
                    maxWidth: 280,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginLeft: 6,
                  }}
                >
                  {evt.title}
                </span>
              </div>
            ))}
          </div>
        )}
        <div
          style={{
            borderRadius: 10,
            padding: 8,
            maxHeight: 220,
            overflowY: 'auto',
            fontSize: 12,
            background: 'rgba(15, 23, 42, 0.9)',
          }}
        >
          {messages.length === 0 ? (
            <span style={{ color: '#6b7280' }}>
              Start by asking something like &ldquo;What are the key risks in TSLA&apos;s
              logistics storylines?&rdquo;
            </span>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {messages.map((m, idx) => (
                <li
                  key={idx}
                  style={{
                    padding: '4px 0',
                    fontWeight: m.role === 'user' ? 500 : 400,
                  }}
                >
                  <strong>{m.role === 'user' ? 'You' : 'Akem'}:</strong>
                  <div style={{ marginTop: 2 }}>
                    {renderMessageContent(m.content)} {/* Used renderMessageContent */}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {error && (
          <div style={{ color: '#f87171', fontSize: 12 }}>{error}</div>
        )}
      </div>

      {/* Floating text field */}
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: 4,
          borderRadius: 9999,
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.25)',
          background: 'rgba(15, 23, 42, 0.96)',
          border: '1px solid rgba(148, 163, 184, 0.6)',
          pointerEvents: 'auto',
        }}
      >
        <input
          style={{
            flex: 1,
            padding: '7px 11px',
            borderRadius: 9999,
            border: 'none',
            fontSize: 13,
            background: 'transparent',
            color: '#e5e7eb',
            outline: 'none',
          }}
          placeholder="Ask about these storylines…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={handleFocusInput}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '6px 14px',
            borderRadius: 9999,
            border: 'none',
            background: loading ? '#6b7280' : '#6366f1',
            color: '#fff',
            fontSize: 13,
            cursor: loading ? 'default' : 'pointer',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          Ask
        </button>
      </form>
    </div>,
    document.body
  );
};
